import type { UserRole } from '@/types/auth'

/**
 * Exhaustive precedence map — a compile error results if any UserRole is missing.
 * Lower index = higher privilege. This is the canonical source of precedence order.
 */
export const ROLE_PRECEDENCE_INDEX: Record<UserRole, number> = {
  SystemAdmin: 0,
  Management: 1,
  Ophthalmologist: 2,
  Doctor: 3,
  Optometrist: 4,
  Nurse: 5,
  Dispensary: 6,
  Accountant: 7,
  Patient: 8,
} as const

/** All roles in precedence order (highest privilege first), derived from ROLE_PRECEDENCE_INDEX. */
export const ROLE_PRECEDENCE: readonly UserRole[] = (
  Object.entries(ROLE_PRECEDENCE_INDEX) as Array<[UserRole, number]>
)
  .sort(([, a], [, b]) => a - b)
  .map(([r]) => r)

/** All known application roles (alias of ROLE_PRECEDENCE). */
export const ALL_ROLES = ROLE_PRECEDENCE

/** Roles that may be assigned as treating clinician (queue/appointment pickers).
 *  No SystemAdmin bypass — treating-clinician is a role-set membership check, not a capability. */
export const TREATING_CLINICIAN_ROLES = [
  'Doctor',
  'Ophthalmologist',
  'Optometrist',
] as const satisfies readonly UserRole[]

/** Roles that may complete a consultation visit (plus domain clinician match). */
export const CONSULTATION_COMPLETING_ROLES = [
  'Doctor',
  'Ophthalmologist',
] as const satisfies readonly UserRole[]

const _TREATING_CLINICIAN_SET: ReadonlySet<UserRole> = new Set(TREATING_CLINICIAN_ROLES)

/**
 * Returns true when at least one of the given roles is a treating-clinician role.
 *
 * Deliberately excludes SystemAdmin — treating-clinician classification is a role-set
 * membership check, not a capability. A SystemAdmin-only profile is NOT a treating clinician
 * and must not appear in clinician picker dropdowns.
 */
export function isTreatingClinicianRole(roles: readonly UserRole[]): boolean {
  return roles.some((r) => _TREATING_CLINICIAN_SET.has(r))
}

export const CLINICAL_STAFF_ROLES: readonly UserRole[] = [
  ...TREATING_CLINICIAN_ROLES,
  'Nurse',
]

export const STAFF_APP_ROLES: readonly UserRole[] = [
  'SystemAdmin',
  'Management',
  ...CLINICAL_STAFF_ROLES,
  'Dispensary',
  'Accountant',
]

/** Staff member roles assignable via the staff UI (excludes SystemAdmin and Patient). */
export const STAFF_ACCOUNT_ROLES = [
  'Management',
  'Doctor',
  'Ophthalmologist',
  'Optometrist',
  'Nurse',
  'Dispensary',
  'Accountant',
] as const satisfies readonly UserRole[]
